/**
 * CoverFlow — ENDLESS station carousel (custom infinite 3D renderer).
 *
 * No beginning, no end: all stations are laid out on a virtual ring using
 * modular arithmetic, so both sides of the active card are always populated.
 * Swipe/click/keyboard/slider wrap round and round in both directions.
 *
 * Props interface is unchanged so pages/Home.tsx needs no edits:
 *  - items: stations in order
 *  - onSelect(item, index): fired on USER navigation (plays the station)
 *  - activeId: external sync (never fires onSelect)
 */

import { useCallback, useEffect, useRef, useState } from "react";

export interface CoverFlowItem {
  id: string;
  image: string;
  title: string;
  subtitle: string;
  badge?: string;
  badgeColor?: string;
  frequency?: string;
  live?: boolean;
}

interface CoverFlowProps {
  items: CoverFlowItem[];
  onSelect?: (item: CoverFlowItem, index: number) => void;
  activeId?: string;
}

/** Shortest signed distance on a ring of n (range roughly -n/2..n/2). */
function ringOffset(i: number, active: number, n: number): number {
  let d = (i - active) % n;
  if (d > n / 2) d -= n;
  if (d < -n / 2) d += n;
  return d;
}

export default function CoverFlow({
  items,
  onSelect,
  activeId,
}: CoverFlowProps) {
  const n = items.length;
  const rootRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(() =>
    Math.max(
      0,
      items.findIndex(i => i.id === activeId)
    )
  );
  const [spacing, setSpacing] = useState(180);

  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;

  // Timestamp until which synthetic clicks are swallowed (set after a swipe
  // ends, so the release doesn't also register as a tap on the card below).
  const suppressClickRef = useRef(0);

  // Swallow the click that follows a completed swipe (mobile fires click on
  // touch-release even after a drag).
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const onClickCapture = (e: MouseEvent) => {
      if (Date.now() < suppressClickRef.current) {
        e.stopPropagation();
        e.preventDefault();
      }
    };
    el.addEventListener("click", onClickCapture, true);
    return () => el.removeEventListener("click", onClickCapture, true);
  }, []);

  // Measure stage to size the 3D spacing responsively.
  useEffect(() => {
    const measure = () => {
      const w = stageRef.current?.clientWidth ?? window.innerWidth;
      setSpacing(Math.max(110, Math.min(250, w * 0.19)));
    };
    measure();
    let t: number | undefined;
    const onResize = () => {
      window.clearTimeout(t);
      t = window.setTimeout(measure, 150);
    };
    window.addEventListener("resize", onResize);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  /** User navigation: normalize round the ring, update, and fire onSelect. */
  const selectIndex = useCallback(
    (t: number) => {
      if (n === 0) return;
      const idx = ((t % n) + n) % n;
      setActiveIndex(prev => {
        if (prev === idx) return prev;
        const it = items[idx];
        if (it) {
          window.setTimeout(() => onSelectRef.current?.(it, idx), 0);
        }
        return idx;
      });
    },
    [n, items]
  );

  const step = useCallback(
    (dir: 1 | -1) => selectIndex(activeIndex + dir),
    [selectIndex, activeIndex]
  );

  // ── External activeId → sync without firing onSelect ──────────────────────
  useEffect(() => {
    if (!activeId) return;
    const idx = items.findIndex(i => i.id === activeId);
    if (idx >= 0) setActiveIndex(idx);
  }, [activeId, items]);

  // ── Pointer-swipe: flick left/right steps round the ring ──────────────────
  // Touch browsers fire pointercancel once they claim a gesture for page
  // scroll — by then pointerup never comes, and the cancel event's clientX/Y
  // can equal the start point. So we follow pointermove for the real drag
  // path, treat cancel as "gesture over" using the last seen position, and
  // let CSS touch-action: pan-y keep horizontal swipes off the page scroller.
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    let startX = 0;
    let startY = 0;
    let lastX = 0;
    let lastY = 0;
    let moved = false;

    const down = (e: PointerEvent) => {
      startX = lastX = e.clientX;
      startY = lastY = e.clientY;
      moved = false;
    };
    const move = (e: PointerEvent) => {
      lastX = e.clientX;
      lastY = e.clientY;
      if (Math.abs(e.clientX - startX) > 8) moved = true;
    };
    const finish = () => {
      const dx = lastX - startX;
      const dy = lastY - startY;
      // Distance + horizontal-dominance gates only; no speed cap — a slow
      // deliberate drag is still a swipe.
      if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy) * 1.2) return;
      // A completed swipe would otherwise end in a stray click on the card
      // under the finger — swallow it briefly.
      suppressClickRef.current = Date.now() + 450;
      step(dx < 0 ? 1 : -1);
    };
    const up = () => {
      if (moved) finish();
    };

    el.addEventListener("pointerdown", down);
    el.addEventListener("pointermove", move);
    el.addEventListener("pointerup", up);
    el.addEventListener("pointercancel", up);
    return () => {
      el.removeEventListener("pointerdown", down);
      el.removeEventListener("pointermove", move);
      el.removeEventListener("pointerup", up);
      el.removeEventListener("pointercancel", up);
    };
  }, [step]);

  // ── Keyboard navigation (wraps) ───────────────────────────────────────────
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (
        e.defaultPrevented ||
        (e.key !== "ArrowLeft" && e.key !== "ArrowRight")
      ) return;
      const target = e.target;
      if (
        target instanceof HTMLElement &&
        (target.isContentEditable || target.closest("input, textarea, select"))
      ) return;
      // Home mounts separate mobile and desktop carousels. Only the visible
      // one should handle a physical key press and request station playback.
      if (!rootRef.current?.getClientRects().length) return;
      step(e.key === "ArrowLeft" ? -1 : 1);
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [step]);

  const activeItem = items[activeIndex] ?? items[0];
  const VISIBLE = 3; // cards visible each side; opposite card hides behind

  return (
    <div ref={rootRef} className="coverflow-root">
      {/* ── Green glow stage background ── */}
      <div className="coverflow-glow-bg" aria-hidden="true" />

      {/* ── Endless 3D ring stage ── */}
      <div className="coverflow-stage-outer">
        <div
          ref={stageRef}
          className="coverflow-stage"
          style={{
            position: "relative",
            height: "100%",
            perspective: "1200px",
          }}
        >
          {items.map((item, i) => {
            const d = ringOffset(i, activeIndex, Math.max(n, 1));
            const ad = Math.abs(d);
            const hidden = ad > VISIBLE;
            const x = d * spacing;
            const z = -ad * 130;
            const rot = Math.max(-58, Math.min(58, -d * 38));
            const scale = d === 0 ? 1 : Math.max(0.55, 0.85 - ad * 0.08);
            return (
              <div
                key={item.id}
                className="coverflow-card"
                data-active={i === activeIndex}
                onClick={() => {
                  if (i !== activeIndex) selectIndex(i);
                }}
                style={{
                  position: "absolute",
                  left: "50%",
                  top: "50%",
                  transform: `translate(calc(-50% + ${x}px), -50%) translateZ(${z}px) rotateY(${rot}deg) scale(${scale})`,
                  transformStyle: "preserve-3d",
                  zIndex: 100 - ad,
                  opacity: hidden ? 0 : Math.max(0.35, 1 - ad * 0.18),
                  pointerEvents: hidden ? "none" : "auto",
                  cursor: i === activeIndex ? "default" : "pointer",
                  transition:
                    "transform 450ms cubic-bezier(0.22,1,0.36,1), opacity 350ms ease",
                }}
              >
                <img
                  src={item.image}
                  alt={item.title}
                  className="coverflow-img"
                  draggable={false}
                />
                {item.frequency && (
                  <span className="coverflow-freq">{item.frequency}</span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Active card info ── */}
      <div className="coverflow-info">
        <div className="coverflow-info-inner">
          {activeItem.badge && (
            <span
              className="coverflow-genre-badge"
              style={{ color: activeItem.badgeColor ?? "#7dc832" }}
            >
              {activeItem.badge}
            </span>
          )}
          <h3 className="coverflow-title">{activeItem.title}</h3>
          <p className="coverflow-subtitle">{activeItem.subtitle}</p>
        </div>
      </div>

      {/* ── Endless scrub slider: wraps round both ends ── */}
      <div className="coverflow-scrub">
        <input
          type="range"
          min={0}
          max={items.length - 1}
          step={1}
          value={activeIndex}
          onChange={e => selectIndex(Number(e.target.value))}
          aria-label="Browse stations (endless loop)"
        />
      </div>
    </div>
  );
}
